// noinspection SpellCheckingInspection

import React from 'react';

export const ResizeHandlePlacementContext = React.createContext<string | null>(null);

export const useResizeHandlePlacement = () => {
  return React.useContext(ResizeHandlePlacementContext);
};

const enabledHandlesMap: Record<string, string[]> = {
  'top-start': ['top', 'topRight', 'right'],
  'top-end': ['top', 'topLeft', 'left'],
  'bottom-end': ['bottom', 'bottomLeft', 'left'],
  'bottom-start': ['bottom', 'bottomRight', 'right'],
};

const handleCursors: Record<string, string> = {
  topLeft: 'nwse-resize',
  top: 'ns-resize',
  topRight: 'nesw-resize',
  right: 'ew-resize',
  bottomRight: 'nwse-resize',
  bottom: 'ns-resize',
  bottomLeft: 'nesw-resize',
  left: 'ew-resize',
};

interface ResizeHandleProps {
  handle: string;
  containerRef: React.RefObject<HTMLElement>;
  children: React.ReactElement<{ onMouseDown?: (e: React.MouseEvent) => void; style?: React.CSSProperties }>;
}

export function ResizeHandle({
  handle,
  containerRef,
  children,
}: Readonly<ResizeHandleProps>): React.ReactElement {
  const placement = useResizeHandlePlacement();

  const enabled = placement
    ? enabledHandlesMap[placement]?.includes(handle)
    : false;

  const getDirectionMultipliers = (): { dx: number; dy: number } => {
    let dx = 1;
    let dy = 1;

    if (handle.toLowerCase().includes("left")) dx = -1;
    if (handle.toLowerCase().includes("top")) dy = -1;

    return { dx, dy };
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (!enabled || !containerRef.current) return;

    e.preventDefault();

    const container = containerRef.current;
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = container.offsetWidth;
    const startHeight = container.offsetHeight;

    const { dx: dxMultiplier, dy: dyMultiplier } = getDirectionMultipliers();

    const onMouseMove = (moveEvent: MouseEvent) => {
      const dx = (moveEvent.clientX - startX) * dxMultiplier;
      const dy = (moveEvent.clientY - startY) * dyMultiplier;

      const newWidth = startWidth + dx;
      const newHeight = startHeight + dy;

      if (handle.toLowerCase().includes("left") || handle.toLowerCase().includes("right")) {
        container.style.width = `${newWidth}px`;
      }
      if (handle.toLowerCase().includes("top") || handle.toLowerCase().includes("bottom")) {
        container.style.height = `${newHeight}px`;
      }
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  return React.cloneElement(children, {
    onMouseDown,
    style: {
      pointerEvents: enabled ? "auto" : "none",
      cursor: enabled ? handleCursors[handle] : "default",
      ...(children.props.style || {}),
    },
  });
}
